export enum StoPayIntentStatus {
    Succeeded = 'succeeded',
    NeedsInformation = 'needsInformation',
    RequiresAction = 'requires_action',
    Failed = 'failed',
    Processing = 'processing',
    Cancelled = 'canceled',
    AuthFailed = 'auth_failed',
    Expired = 'expired',
}
export enum StoPayIntentErrorCode {
    SurchargeError = 'surcharge_error',
    PaymentAuthFailure = 'payment_intent_authentication_failure',
}

export enum StoPayIntentAction {
    Initiated = 'initiated',
    Cancelled = 'cancelled',
    Retry = 'retry',
    Completed = 'completed',
    AuthFailed = 'auth_failed',
}

export enum StoPaymentProcessor {
    Payrix = 'Payrix',
    Stripe = 'Stripe',
}

export enum StoPayLocale {
    En = 'en',
    FrCa = 'fr-CA',
}
export enum Currency {
    USD = 'usd',
    CAD = 'cad',
    AUD = 'aud',
    GBP = 'gbp',
}

export enum PaymentIntentUsageType {
    Moto = 'moto',
    CustomerInitiated = 'customerInitiated',
    MerchantInitiated = 'merchantInitiated',
}

export enum StopayPaymentMethod {
    CARD = 'card',
    BACS = 'bacs',
    BECS = 'becs',
    ACH = 'ach',
    WALLET = 'wallet',
}

export enum StripePaymentMethod {
    CARD = 'card',
    BACS = 'bacs_debit',
    BECS = 'au_becs_debit',
    ACH = 'us_bank_account',
}

export enum CardType {
    Card = 'card',
}

export enum CardFundingType {
    Credit = 'credit',
    Debit = 'debit',
    Prepaid = 'prepaid',
    Unknown = 'unknown',
}

export enum CardBrand {
    Amex = 'amex',
    Visa = 'visa',
    MasterCard = 'mastercard',
    Discover = 'discover',
    Jcb = 'jcb',
    Diners = 'diners',
    Unknown = 'unknown',
    Unionpay = 'unionpay',
}

export enum StoPayTheme {
    // theme that is most suitable for StorEdge, like Win95
    // "w95" className is being added to the root container and appropriate styles are defined
    // in the  app/payment-app/app/payment-widget.module.scss
    // if you would like to rename - need also rename w95 class name in .scss
    W95 = 'w95',
    Default = 'default',
    Se_Default = 'se_default',
}

// export enum StoPayMFEMessageType {
//     PaymentProcessingStatus = 'PaymentProcessingStatus',
//     UpdateSettings = 'UpdateSettings', // this event type can be used for sending updated MFE size
// }

export enum StoPayMFEMessageType {
    IntentProcessing = 'intent_processing',
    IntentResult = 'intent_result',
    IntentCancel = 'intent_cancel',
    IntentRetry = 'intent_retry',
    IntentAuthFailed = 'intent_auth_failed',
    IntentUpdate = 'intent_update',
    ProcessPayment = 'process_payment',
    paymentMethodUpdate = 'payment_method_update',
    intentError = 'intent_error',
    SavePayment = 'save_payment',
    CancelPayment = 'cancel_payment',
    AutoPay = 'auto_pay',
    AuthToken = 'auth_token',
    AuthTokenRequest = 'required_auth',
    MfeReady = 'mfe_ready',
    SavePaymentMethod = 'save_payment_method',
}

export const IntentStatusToMessageTypeMap: Record<
    StoPayIntentAction,
    StoPayMFEMessageType
> = {
    [StoPayIntentAction.Initiated]: StoPayMFEMessageType.IntentProcessing,
    [StoPayIntentAction.Completed]: StoPayMFEMessageType.IntentResult,
    [StoPayIntentAction.Cancelled]: StoPayMFEMessageType.IntentCancel,
    [StoPayIntentAction.Retry]: StoPayMFEMessageType.IntentRetry,
    [StoPayIntentAction.AuthFailed]: StoPayMFEMessageType.IntentAuthFailed,
};

export const StripePaymentMethodMap: Record<
    StopayPaymentMethod,
    StripePaymentMethod
> = {
    [StopayPaymentMethod.CARD]: StripePaymentMethod.CARD,
    [StopayPaymentMethod.BACS]: StripePaymentMethod.BACS,
    [StopayPaymentMethod.BECS]: StripePaymentMethod.BECS,
    [StopayPaymentMethod.ACH]: StripePaymentMethod.ACH,
    [StopayPaymentMethod.WALLET]: StripePaymentMethod.CARD, // Default to card for wallet
};

export interface IStoPayIntent {
    intentId: string;
    url?: string;
    processor: StoPaymentProcessor;
    amount: number;
    locale: StoPayLocale;
    currency: string;
    stripe?: object;
    payrix?: object;
}

export interface ConfigurationProps {
    theme: string;
    returnUrl: string;
    showAutoPay: boolean;
    showPayButton: boolean;
    showSaveButton: boolean;
    saveButtonLabel: string;
    showBillingAddress: boolean;
    showCancelButton: boolean;
    showSaveCheckbox: boolean;
    mode: 'default' | 'save' | 'pay';
    showDigitalWallets: boolean;
    disableVerificationForAch: boolean;
    surchargePercentage?: number;
}

export interface IMockedStoPayIntents {
    [key: string]: IStoPayIntent;
}

interface MFEParamsPayload {
    [key: string]: any;
}
export interface StoPayMFEUrlParams {
    processor: StoPaymentProcessor;
    locale?: StoPayLocale;
    theme?: StoPayTheme;
    payload: MFEParamsPayload;
    amount: number;
}

export enum StripeIntentType {
    Payment = 'payment',
    Setup = 'setup',
    SetupAndPayment = 'setup_and_payment',
}

export enum MfeButtonAction {
    PAY = 'pay',
    SAVE = 'save',
    CANCEL = 'cancel',
}

export interface BillingDetails {
    name?: string;
    phone?: string;
    address?: {
        line1: string;
        line2: string;
        city: string;
        state: string;
        postal_code: string;
        country: string;
    };
}

export interface SetupIntentResponse {
    clientSecret: string;
}

export interface PaymentIntentResponse {
    clientSecret: string;
}
export interface StripeError {
    code?: string;
    message?: string;
}

interface Address {
    address1: string;
    address2: string;
    city: string;
    state: string;
    country: string;
    zip: string;
    firstName: string;
    lastName: string;
}

interface CreditCard {
    cardType: string;
    expirationMonth: number;
    expirationYear: number;
    last4Digits: string;
}

interface BankAccountDetails {
    accountNumber: string;
}

export interface Token {
    id: string;
    bankAccount: BankAccountDetails;
    processorTokenValue: string;
    billingAddress: Address;
    creditCard: CreditCard;
    surchargeEnabled: boolean;
    hasBeenAuthorized: boolean;
}

export enum PaymentIntentBehavior {
    Pay = 'pay',
    Save = 'save',
    PayAndSave = 'payAndSave',
}

export enum AccountHolderType {
    Individual = 'individual',
    Company = 'company',
}

export enum StripeIntentStatus {
    RequiresAction = 'requires_action',
    Processing = 'processing',
    Succeeded = 'succeeded',
    RequiresPaymentMethod = 'requires_payment_method',
    RequiresConfirmation = 'requires_confirmation',
    Canceled = 'canceled',
}

export type StopayStatusValidKeys =
    | StoPayIntentStatus.Cancelled
    | StoPayIntentStatus.Succeeded
    | StoPayIntentStatus.Processing
    | StoPayIntentStatus.Failed
    | StoPayIntentStatus.Expired
    | StoPayIntentStatus.RequiresAction;

export const debitMethods = new Set([
    StopayPaymentMethod.BACS,
    StopayPaymentMethod.BECS,
    StopayPaymentMethod.ACH,
]);
